import { useState } from 'react';
import { apiClient, useAuth } from '../../auth-lib';
import { Button, Card, Input, Select, StatusPill, useToast } from '../../design-system';

const MANAGER_LEVEL = 3;
const emptyLog = { employeeId: '', hours: '', note: '' };

// Timelog tab for the Task Detail Dashboard. Preserves the existing
// hour-tracking + time-logging behaviour (same endpoints and rules as the
// shared TaskDetail page) so the redesign doesn't drop the feature.
function TaskTimelogTab({ task, setTask }) {
  const { user } = useAuth();
  const toast = useToast();
  const [logForm, setLogForm] = useState(emptyLog);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const isManager = (user?.role?.level || 0) >= MANAGER_LEVEL;
  // Only a manager may attribute time to someone else; everyone else logs
  // against themselves. The person who actually did the work is usually NOT a
  // manager, so gating the whole form on this left the preparer with no way to
  // record their own hours.
  const canLogForOthers = isManager;
  const myId = user?._id || user?.id;

  const hourTracking = (task.hourAllocations || []).map((allocation) => {
    const userId = allocation.userId?._id || allocation.userId;
    const actualMinutes = (task.timeLogs || [])
      .filter((log) => (log.employeeId?._id || log.employeeId) === userId)
      .reduce((sum, log) => sum + (log.minutes || 0), 0);
    const actualHours = actualMinutes / 60;
    const variance = actualHours - allocation.allocatedHours;
    let varianceStatus = 'success';
    if (allocation.allocatedHours > 0) {
      if (variance > 0) varianceStatus = 'danger';
      else if (actualHours / allocation.allocatedHours >= 0.9) varianceStatus = 'warning';
    }
    return {
      key: `${userId}-${allocation.role}`,
      name: allocation.userId?.name || 'Unknown',
      role: allocation.role,
      allocatedHours: allocation.allocatedHours,
      actualHours,
      variance,
      varianceStatus,
    };
  });

  // Who a manager may attribute time to. The server only accepts this task's
  // preparer/reviewer/assistants for someone ELSE — plus the caller themselves,
  // always, which is why "Myself" is appended when the manager isn't already on
  // the task. Without it, logging on a task with no assignees was impossible.
  const assignedPeople = [
    task.preparerId?.name && { id: task.preparerId._id, name: task.preparerId.name, role: 'Preparer' },
    task.reviewerId?.name && { id: task.reviewerId._id, name: task.reviewerId.name, role: 'Reviewer' },
    ...(task.assistantIds || []).filter((a) => a?.name).map((a) => ({ id: a._id, name: a.name, role: 'Assistant' })),
  ].filter(Boolean);
  const loggablePeople = assignedPeople.some((p) => p.id === myId)
    ? assignedPeople
    : [...assignedPeople, { id: myId, name: user?.name || 'Me', role: 'Myself' }];

  const totalLoggedMinutes = (task.timeLogs || []).reduce((sum, l) => sum + (l.minutes || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    // A manager picks who the time belongs to; everyone else is implicitly
    // logging their own, so there's nothing to pick.
    const targetId = canLogForOthers ? logForm.employeeId : myId;
    if (!targetId || !logForm.hours) {
      toast.danger(canLogForOthers ? 'Pick a person and enter hours' : 'Enter hours');
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.post(`/api/tasks/${task._id}/timelogs`, {
        employeeId: targetId,
        minutes: Math.round(Number(logForm.hours) * 60),
        note: logForm.note || undefined,
      });
      setTask(res.data);
      setLogForm(emptyLog);
      toast.success('Time logged');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to log time');
    } finally {
      setSaving(false);
    }
  };

  // Undo a mis-entered log. The server allows your own, or any if you're a
  // manager — mirrored here so the × only shows where it will actually work.
  const canRemoveLog = (log) => isManager || (log.employeeId?._id || log.employeeId) === myId;

  const removeLog = async (logId) => {
    if (!window.confirm('Remove this time entry?')) return;
    setRemovingId(logId);
    try {
      const res = await apiClient.delete(`/api/tasks/${task._id}/timelogs/${logId}`);
      setTask(res.data);
      toast.success('Time entry removed');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove time entry');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="td-timelog">
      <Card padding="md">
        <div className="td-card-head">
          <span className="td-card-title">Hour Tracking</span>
          <span className="td-muted">{(totalLoggedMinutes / 60).toFixed(2)}h logged total</span>
        </div>
        {hourTracking.length === 0 ? (
          <div className="td-muted">No hour allocations set for this task.</div>
        ) : (
          <div className="td-hourtrack">
            {hourTracking.map((entry) => (
              <div key={entry.key} className="td-hourtrack-row">
                <div className="td-hourtrack-person">
                  <span className="td-hourtrack-name">{entry.name}</span>
                  <span className="td-hourtrack-role">{entry.role}</span>
                </div>
                <div className="td-hourtrack-hours">
                  <span>{entry.actualHours.toFixed(2)}h / {entry.allocatedHours}h</span>
                  <StatusPill
                    variant={entry.varianceStatus}
                    label={entry.variance > 0 ? `+${entry.variance.toFixed(2)}h over` : `${Math.abs(entry.variance).toFixed(2)}h under`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* The picker is manager-only; the form itself is for everyone, so the
            preparer can record the hours they actually worked. */}
        <form onSubmit={submit} className="td-log-form">
          {canLogForOthers && (
            <Select value={logForm.employeeId} onChange={(e) => setLogForm({ ...logForm, employeeId: e.target.value })} disabled={saving}>
              <option value="">-- Who logged time? --</option>
              {loggablePeople.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
            </Select>
          )}
          <Input type="number" min="0" step="0.25" placeholder="Hours" value={logForm.hours} onChange={(e) => setLogForm({ ...logForm, hours: e.target.value })} disabled={saving} />
          <Input type="text" placeholder="Note (optional)" value={logForm.note} onChange={(e) => setLogForm({ ...logForm, note: e.target.value })} disabled={saving} />
          <Button type="submit" variant="secondary" size="sm" loading={saving}>
            {canLogForOthers ? 'Log Time' : 'Log My Time'}
          </Button>
        </form>
      </Card>

      <Card padding="md">
        <div className="td-card-head"><span className="td-card-title">Time Log</span></div>
        {(task.timeLogs || []).length === 0 ? (
          <div className="td-muted">No time logged yet.</div>
        ) : (
          <div className="td-log-list">
            {[...(task.timeLogs || [])].reverse().map((log) => (
              <div key={log._id} className="td-log-row">
                <span className="td-log-who">{log.employeeId?.name || 'Someone'}</span>
                <span className="td-log-hours">{(log.minutes / 60).toFixed(2)}h</span>
                <span className="td-log-note">{log.note || ''}</span>
                <span className="td-muted">{new Date(log.loggedAt).toLocaleDateString()}</span>
                {canRemoveLog(log) ? (
                  <button
                    type="button"
                    className="td-log-remove"
                    onClick={() => removeLog(log._id)}
                    disabled={removingId === log._id}
                    aria-label="Remove time entry"
                    title="Remove this entry"
                  >
                    ×
                  </button>
                ) : (
                  <span className="td-log-remove-spacer" />
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default TaskTimelogTab;
